import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Bot, RefreshCw, Search, Sparkles, ExternalLink, Phone, Send, FileText, Gift, DollarSign, ChevronDown, ChevronUp, ScrollText, FlaskConical, ShieldAlert, Ban } from "lucide-react";
import ReminderDialog from "./ReminderDialog";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_ICONS = { new_requests: Sparkles, follow_up: Send, payments: DollarSign, docs: FileText, demand: Bot, club: Gift, referrals: Gift, maintenance: Bot };

function ActionButtons({ item, onNavigate, onReminder }) {
  const acts = item.actions || [];
  return (
    <div className="flex gap-1.5 shrink-0">
      {acts.includes("reminder") && item.res && (
        <button data-testid={`ai-act-reminder-${item.res.id}`} onClick={() => onReminder(item.res)}
          className="px-2 py-1 rounded bg-[#F2B705]/15 text-[#F2B705] text-[10px] font-bold hover:bg-[#F2B705] hover:text-black transition-colors">Send Reminder</button>
      )}
      {acts.includes("collect") && (
        <button onClick={() => onNavigate("bookings")} className="px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 text-[10px] font-bold">Collect Payment</button>
      )}
      {acts.includes("docs") && (
        <button onClick={() => onNavigate("reservations")} className="px-2 py-1 rounded bg-sky-500/15 text-sky-300 text-[10px] font-bold">Request Documents</button>
      )}
      {acts.includes("loyalty") && (
        <button onClick={() => onNavigate("marketing")} className="px-2 py-1 rounded bg-purple-500/15 text-purple-300 text-[10px] font-bold">Send Loyalty Offer</button>
      )}
      {acts.includes("call") && item.res?.phone && (
        <a href={`tel:${item.res.phone}`} className="px-2 py-1 rounded bg-white/5 text-white/60 text-[10px] font-bold flex items-center gap-1"><Phone size={9} /> Call</a>
      )}
    </div>
  );
}

function FraudWatch({ token, onNavigate }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [d, setD] = useState(null);
  const load = () => axios.get(`${API}/fraud/watch`, H).then((r) => setD(r.data)).catch(() => setD({ total_flagged: 0, items: [] }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  const dnr = async (it) => {
    const reason = window.prompt(`Add ${it.name} to the DO-NOT-RENT list?\nAll future requests/bookings from this email/phone will be blocked.\n\nType the reason to confirm:`);
    if (!reason) return;
    const decline = window.confirm("Also decline their open request(s) right now?\n\nOK = block + decline. Cancel = block only.");
    try {
      await axios.post(`${API}/reservations/${it.id}/dnr`, { reason, decline }, H);
      toast.success(`${it.name} added to Do-Not-Rent${decline ? " and declined" : ""}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add to Do-Not-Rent."); }
  };
  return (
    <div className="bg-[#0C0C0C] border border-orange-500/25 rounded-xl p-5" data-testid="fraud-watch">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert size={15} className="text-orange-400" />
        <span className="text-white font-semibold text-sm">Fraud Watch</span>
        {d && <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${d.total_flagged > 0 ? "bg-orange-500/20 text-orange-300" : "bg-green-500/15 text-green-400"}`} data-testid="fraud-watch-count">{d.total_flagged > 0 ? `${d.total_flagged} flagged` : "All clear"}</span>}
        <button onClick={load} className="ml-auto text-white/40 hover:text-white"><RefreshCw size={12} /></button>
      </div>
      <p className="text-white/35 text-[11px] mb-3">Duplicate requests, multiple names on one phone/email, rapid-fire submissions, throwaway emails, past declined/blocked attempts and overlapping date requests. Advisory only — you make every call.</p>
      {!d ? <Loader2 size={14} className="animate-spin text-white/40" /> : d.items.length === 0 ? (
        <p className="text-white/45 text-xs">No suspicious activity detected across open reservation requests. ✅</p>
      ) : (
        <div className="space-y-2">
          {d.items.map((it) => (
            <div key={it.id} data-testid={`fraud-item-${it.id}`} className="bg-black/40 border border-orange-500/15 rounded-lg px-3.5 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-white/90 text-sm font-semibold">{it.name}</span>
                <span className="text-white/35 text-[11px]">{it.phone} · {it.email} · {it.status}</span>
                <div className="ml-auto flex gap-1.5">
                  <button onClick={() => onNavigate("reservations", it.id)} className="px-2.5 py-1 rounded-md border border-white/15 text-white/60 hover:text-white text-[10px] font-bold flex items-center gap-1">Open <ExternalLink size={9} /></button>
                  <button data-testid={`fraud-dnr-${it.id}`} onClick={() => dnr(it)} className="px-2.5 py-1 rounded-md bg-red-500/15 text-red-300 text-[10px] font-bold hover:bg-red-500 hover:text-white transition-colors flex items-center gap-1"><Ban size={9} /> Do Not Rent</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {it.flags.map((f) => (
                  <span key={f.code} title={f.detail} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-300 text-[10px] font-semibold">
                    <ShieldAlert size={9} /> {f.label}
                  </span>
                ))}
              </div>
              <div className="text-white/40 text-[11px] mt-1">{it.flags.map((f) => f.detail).join(" ")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditLog({ token }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const load = () => axios.get(`${API}/audit-log?limit=80`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setRows(r.data)).catch(() => setRows([]));
  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl" data-testid="audit-log">
      <button onClick={() => { setOpen(!open); if (!rows) load(); }} className="w-full flex items-center gap-2 px-5 py-3 text-left">
        <ScrollText size={15} className="text-[#F2B705]" />
        <span className="text-white font-semibold text-sm">Audit Log</span>
        <span className="text-white/35 text-xs">every reminder, archive, delete, bulk action, AI draft & status change</span>
        {open ? <ChevronUp size={14} className="ml-auto text-white/40" /> : <ChevronDown size={14} className="ml-auto text-white/40" />}
      </button>
      {open && (
        <div className="border-t border-white/5 px-5 py-3 max-h-72 overflow-y-auto space-y-1">
          {!rows ? <Loader2 size={14} className="animate-spin text-white/40" /> : rows.length === 0 ? <p className="text-white/40 text-xs">No activity logged yet.</p> :
            rows.map((a) => (
              <div key={a.id} className="text-xs text-white/60">
                <span className="text-white/30">{fmtDateTime(a.at)}</span>
                <span className="text-[#F2B705]/80 mx-1.5">{a.action}</span>
                <span className="text-white/45">{a.actor}</span> — {a.detail || a.entity_id}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function TestReply({ token, onDone }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState("YES");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/sms/inbound/test`, { phone, message: msg }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(data.matched ? `Test reply processed → ${data.new_status || "logged"} (reservation ${data.reservation_id?.slice(0, 8)})`
        : `Test reply processed — no open reservation matched that phone.`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl" data-testid="sms-test-mode">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-5 py-3 text-left">
        <FlaskConical size={15} className="text-[#F2B705]" />
        <span className="text-white font-semibold text-sm">SMS Reply Test Mode</span>
        <span className="text-white/35 text-xs">simulate YES / NO / STOP without touching real customers</span>
        {open ? <ChevronUp size={14} className="ml-auto text-white/40" /> : <ChevronDown size={14} className="ml-auto text-white/40" />}
      </button>
      {open && (
        <div className="border-t border-white/5 px-5 py-3 flex flex-wrap items-end gap-3">
          <label className="text-xs text-white/50">Test phone (use a test reservation's phone)
            <input data-testid="sms-test-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="3145551234"
              className="block mt-1 bg-black border border-white/15 rounded px-2 py-1.5 text-white text-sm w-44" />
          </label>
          <label className="text-xs text-white/50">Reply text
            <select data-testid="sms-test-msg" value={msg} onChange={(e) => setMsg(e.target.value)} className="block mt-1 bg-black border border-white/15 rounded px-2 py-1.5 text-white text-sm">
              <option>YES</option><option>NO</option><option>STOP</option>
            </select>
          </label>
          <button data-testid="sms-test-run" onClick={run} disabled={busy || !phone}
            className="px-4 py-2 rounded-md bg-[#F2B705] text-black font-bold text-xs disabled:opacity-40">
            {busy ? "Processing…" : "Simulate Reply"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function AiAssistant({ token, onNavigate }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [d, setD] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [reminderRes, setReminderRes] = useState(null);

  const load = (refresh = false) => {
    setRefreshing(true);
    return axios.get(`${API}/assistant/briefing${refresh ? "?refresh=true" : ""}`, H)
      .then((r) => setD(r.data)).catch(() => toast.error("Could not load the briefing."))
      .finally(() => setRefreshing(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setAsking(true); setAnswer(null);
    try {
      const { data } = await axios.post(`${API}/assistant/ask`, { question: text }, H);
      setAnswer(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI search failed."); }
    finally { setAsking(false); }
  };

  const SUGGESTIONS = ["Who needs a follow-up today?", "Who hasn't rented in 90 days?", "Which car made the most money this month?",
    "Show customers waiting on documents", "Who has the highest lifetime value?", "Which marketing source generated the most leads?"];

  if (!d) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Waking up your AI business manager…</div>;

  return (
    <div className="space-y-5" data-testid="ai-assistant">
      <div className="flex items-center gap-2">
        <h3 className="font-display font-bold text-white flex items-center gap-2 mr-auto"><Bot size={17} className="text-[#F2B705]" /> AI Owner Assistant</h3>
        <span className="text-white/30 text-[11px]">model: {d.model}</span>
        <button data-testid="briefing-refresh" onClick={() => load(true)} disabled={refreshing}
          className="flex items-center gap-1.5 text-xs border border-white/15 px-3 py-1.5 rounded-md text-white/60 hover:text-white disabled:opacity-50">
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh Briefing
        </button>
      </div>

      {d.summary && (
        <div className="bg-gradient-to-r from-[#F2B705]/10 to-transparent border border-[#F2B705]/25 rounded-xl p-5" data-testid="briefing-summary">
          <div className="text-[#F2B705] text-xs font-bold uppercase tracking-widest mb-1.5">Good morning — your daily briefing</div>
          <p className="text-white/85 text-sm leading-relaxed">{d.summary}</p>
        </div>
      )}

      {/* AI Search */}
      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="ai-search">
        <div className="flex gap-2">
          <input data-testid="ai-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="Ask anything… e.g. Which car made the most money this month?"
            className="flex-1 bg-black border border-white/15 rounded-md px-3 py-2 text-white text-sm" />
          <button data-testid="ai-search-btn" onClick={() => ask()} disabled={asking}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50">
            {asking ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Ask
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => { setQ(s); ask(s); }} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/50 hover:text-white text-[11px]">{s}</button>
          ))}
        </div>
        {answer && (
          <div className="mt-3 bg-black/50 border border-white/10 rounded-md p-3.5" data-testid="ai-search-answer">
            <p className="text-white/85 text-sm whitespace-pre-line">{answer.answer}</p>
            {answer.links?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {answer.links.map((l, i) => (
                  <button key={i} onClick={() => onNavigate(l.tab)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#F2B705]/10 border border-[#F2B705]/25 text-[#F2B705] text-[11px] font-semibold hover:bg-[#F2B705] hover:text-black transition-colors">
                    <ExternalLink size={10} /> {l.label || l.ref}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recommendations */}
      {d.recommendations?.length > 0 && (
        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="ai-recommendations">
          <h4 className="text-sm font-semibold text-white/80 mb-3 flex items-center gap-2"><Sparkles size={14} className="text-[#F2B705]" /> Today's Recommendations</h4>
          <div className="space-y-2">
            {d.recommendations.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5">
                <div className="min-w-0">
                  <div className="text-white/90 text-sm font-semibold">{r.title}</div>
                  <div className="text-white/40 text-xs mt-0.5">Why: {r.reason}</div>
                </div>
                <button onClick={() => onNavigate(r.link?.tab || "command")}
                  className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/15 text-white/60 hover:text-white text-xs">
                  Open <ExternalLink size={10} />
                </button>
              </div>
            ))}
          </div>
          <p className="text-white/30 text-[10px] mt-2.5">The AI recommends — you stay in control. No customer message is ever sent without your approval, and the AI can never delete records.</p>
        </div>
      )}

      {/* Briefing sections */}
      <div className="grid md:grid-cols-2 gap-4">
        {d.sections.filter((s) => s.count > 0).map((s) => {
          const Icon = SECTION_ICONS[s.key] || Bot;
          return (
            <div key={s.key} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4" data-testid={`briefing-${s.key}`}>
              <div className="flex items-center gap-2 mb-2.5">
                <Icon size={14} className="text-[#F2B705]" />
                <span className="text-white font-semibold text-sm">{s.label}</span>
                <span className="ml-auto px-2 py-0.5 rounded-full bg-[#F2B705]/15 text-[#F2B705] text-xs font-bold">{s.count}</span>
              </div>
              <div className="space-y-1.5">
                {s.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs bg-black/30 rounded-md px-2.5 py-2">
                    <div className="min-w-0">
                      <span className="text-white/85 font-semibold">{it.title}</span>
                      <div className="text-white/40 truncate">{it.reason}</div>
                    </div>
                    <ActionButtons item={it} onNavigate={onNavigate} onReminder={setReminderRes} />
                  </div>
                ))}
                {s.items.length < s.count && <button onClick={() => onNavigate(s.items[0]?.link?.tab || "reservations")} className="text-[#F2B705]/70 text-[11px]">+ {s.count - s.items.length} more — open the full list</button>}
              </div>
            </div>
          );
        })}
      </div>

      <FraudWatch token={token} onNavigate={onNavigate} />
      <TestReply token={token} />
      <AuditLog token={token} />

      {reminderRes && <ReminderDialog r={reminderRes} token={token} onClose={() => setReminderRes(null)} onSent={() => load()} />}
    </div>
  );
}
